import React from 'react'
import { useNavigate } from 'react-router-dom'
import { Printer, Download, Mail } from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import ManagementFiltersBar from './ManagementFiltersBar'
import { ErrorState, HealthBadge, Loading } from './ManagementWidgets'
import { EMAIL_DISABLED_NOTE, formatDate, formatDateTime, printReport } from './managementUtils'
import './Management.css'

const ManagementReportShell = ({
  title,
  subtitle,
  report,
  canExport,
  onExport,
  headerExtra,
  overallHealth,
  children
}) => {
  const navigate = useNavigate()
  const { filters, setFilters, loading, error, data, lastRefresh, completeness, reload, can } = report
  const exportAllowed = canExport ?? can('EXPORT')
  const period = data?.period

  const breadcrumbItems = [
    { label: 'Management', path: '/management?tab=reports', onClick: () => navigate('/management?tab=reports') },
    { label: title, isCurrent: true }
  ]

  return (
    <div className="sales-dashboard management-report">
      <div className="breadcrumb-section no-print">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="mis-report-header">
        <div>
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
          <div className="mis-report-period">
            {period?.label && <span className="mis-period-chip">{period.label}</span>}
            {period?.asOf && <span>As of {formatDate(period.asOf)}</span>}
            {period?.financialYear && <span>FY {period.financialYear}</span>}
            {overallHealth && <span>Overall <HealthBadge status={overallHealth} /></span>}
            <span className="print-only">Generated {formatDateTime(lastRefresh || new Date())}</span>
          </div>
        </div>
        <div className="mis-report-actions no-print">
          {headerExtra}
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={printReport} disabled={!data}>
            <Printer size={14} /> Print
          </button>
          {exportAllowed && onExport && (
            <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => onExport(data)} disabled={!data}>
              <Download size={14} /> Export
            </button>
          )}
          <span className="mis-email-disabled" title={EMAIL_DISABLED_NOTE}>
            <button type="button" className="btn btn-sm btn-outline-secondary" disabled aria-label={EMAIL_DISABLED_NOTE}>
              <Mail size={14} /> Email
            </button>
          </span>
        </div>
      </div>

      <ManagementFiltersBar
        filters={filters}
        onChange={setFilters}
        onRefresh={() => reload()}
        lastRefresh={lastRefresh}
        completeness={completeness}
        loading={loading}
      />

      {loading && !data && <Loading />}
      {!loading && error && <ErrorState message={error} onRetry={() => reload()} />}
      {data && (
        <div className={`mis-report-body ${loading ? 'mis-refreshing' : ''}`}>
          {children(data)}
        </div>
      )}
    </div>
  )
}

export default ManagementReportShell
